import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  History,
  Layers,
  Pencil,
  PlayCircle,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared/page-header";
import { useTranslation } from "@/i18n/context";
import { ROUTES } from "@/lib/constants";
import {
  SKILL_TEMPLATES,
  templateToPayload,
} from "@/playground/skill-templates";
import { usePlaygroundCatalog } from "@/playground/use-catalog";
import {
  type Skill,
  type SkillVersion,
  SkillsApi,
  type AppendVersionPayload,
  type CreateSkillPayload,
  usePlaygroundSkills,
} from "@/playground/use-skills";

/**
 * /skills — first-class governance surface for workspace skills.
 *
 * Each skill is listed with its active version pill + version count +
 * last updated, plus deep links to:
 *   - "Try in Playground" (→ /playground?skill=<id>)
 *   - "Evaluation" (→ /evaluation?skill=<id>)
 *
 * Operators can:
 *   - Create from scratch (empty form)
 *   - Create from one of the 5 rich AI-governance templates
 *   - Edit metadata (name/description)
 *   - Append a new immutable version (prompt + model + notes)
 *   - Expand a row → view version history + promote/rollback
 *   - Soft-delete (preserves audit row references)
 *
 * Backend (v0.35.0 SkillsApi) is reused as-is; this page is purely a
 * richer surface over the existing endpoints.
 */
export default function SkillsPage() {
  const { t } = useTranslation();
  const { skills, loading, error, refetch } = usePlaygroundSkills();
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState<CreateSkillPayload | null>(
    null
  );
  const [editTarget, setEditTarget] = useState<Skill | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [versionsBySkill, setVersionsBySkill] = useState<
    Record<string, SkillVersion[] | undefined>
  >({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const openCreate = useCallback((initial?: CreateSkillPayload) => {
    setCreateInitial(initial ?? null);
    setCreateOpen(true);
  }, []);

  const loadVersions = useCallback(async (skillId: string) => {
    const data = await SkillsApi.listVersions(skillId);
    setVersionsBySkill((prev) => ({ ...prev, [skillId]: data.versions }));
  }, []);

  const toggleExpanded = useCallback(
    async (skillId: string) => {
      const next = expandedId === skillId ? null : skillId;
      setExpandedId(next);
      if (next && !versionsBySkill[next]) {
        await loadVersions(next);
      }
    },
    [expandedId, versionsBySkill, loadVersions]
  );

  const deleteSkill = useCallback(
    async (skill: Skill) => {
      if (!window.confirm(t("skills.page.confirmDelete"))) return;
      setBusyId(skill.id);
      try {
        await SkillsApi.remove(skill.id);
        refetch();
      } finally {
        setBusyId(null);
      }
    },
    [refetch, t]
  );

  const promoteVersion = useCallback(
    async (skill: Skill, version: number) => {
      setBusyId(skill.id);
      try {
        await SkillsApi.setCurrentVersion(skill.id, version);
        await Promise.all([refetch(), loadVersions(skill.id)]);
      } finally {
        setBusyId(null);
      }
    },
    [refetch, loadVersions]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("skills.page.title")}
        description={t("skills.page.description")}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <TemplatePicker onPick={(payload) => openCreate(payload)} />
            <Button
              variant="default"
              size="sm"
              onClick={() => openCreate(undefined)}
            >
              <Plus size={12} />
              {t("skills.page.newButton")}
            </Button>
          </div>
        }
      />

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
          {error}
        </div>
      )}

      {loading && (
        <p className="text-[12px] text-neutral-500">
          {t("skills.page.loading")}
        </p>
      )}

      {!loading && skills.length === 0 && (
        <EmptyState
          onCreate={() => openCreate(undefined)}
          onTemplate={openCreate}
        />
      )}

      {skills.length > 0 && (
        <ul className="space-y-2">
          {skills.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              expanded={expandedId === skill.id}
              versions={versionsBySkill[skill.id]}
              busy={busyId === skill.id}
              onToggle={() => toggleExpanded(skill.id)}
              onEdit={() => setEditTarget(skill)}
              onDelete={() => deleteSkill(skill)}
              onPromote={(v) => promoteVersion(skill, v)}
            />
          ))}
        </ul>
      )}

      <CreateSkillDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateInitial(null);
        }}
        initial={createInitial}
        onSaved={() => {
          setCreateOpen(false);
          setCreateInitial(null);
          refetch();
        }}
      />
      {editTarget && (
        <EditSkillDialog
          open={!!editTarget}
          skill={editTarget}
          onClose={() => setEditTarget(null)}
          onChanged={() => {
            refetch();
            void loadVersions(editTarget.id);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Skill card
// ============================================================================

function SkillCard({
  skill,
  expanded,
  versions,
  busy,
  onToggle,
  onEdit,
  onDelete,
  onPromote,
}: {
  skill: Skill;
  expanded: boolean;
  versions: SkillVersion[] | undefined;
  busy: boolean;
  onToggle(): void;
  onEdit(): void;
  onDelete(): void;
  onPromote(version: number): void;
}) {
  const { t } = useTranslation();
  const versionCount = versions ? versions.length : null;
  return (
    <li className="rounded-lg border border-neutral-200 bg-white">
      <div className="flex items-start gap-3 p-4">
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 inline-flex h-5 w-5 items-center justify-center text-neutral-400 hover:text-neutral-700"
          aria-label={expanded ? t("skills.page.collapse") : t("skills.page.expand")}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-[14px] font-medium text-neutral-900">
              {skill.name}
            </h3>
            <span
              className="rounded bg-ocean-50 px-1.5 py-[1px] font-mono text-[10.5px] text-ocean-800"
              title={t("skills.page.activeVersionTitle", {
                v: skill.current_version ?? "?",
              })}
            >
              v{skill.current_version ?? "?"} {t("skills.page.activeTag")}
            </span>
            {versionCount !== null && versionCount > 1 && (
              <span className="rounded bg-neutral-100 px-1.5 py-[1px] font-mono text-[10.5px] text-neutral-600">
                {t("skills.page.versionCount", { n: versionCount })}
              </span>
            )}
          </div>
          {skill.description && (
            <p className="mt-1 text-[12px] text-neutral-600">
              {skill.description}
            </p>
          )}
          <div className="mono mt-1 flex flex-wrap items-center gap-3 text-[10.5px] text-neutral-500">
            <span>{skill.slug}</span>
            <span>
              {t("skills.page.lastUpdated", {
                when: new Date(skill.updated_at).toLocaleString(),
              })}
            </span>
            <Link
              to={`${ROUTES.EVALUATION}?skill=${encodeURIComponent(skill.id)}`}
              className="inline-flex items-center gap-1 text-ocean-700 transition-colors hover:text-ocean-800"
            >
              <CheckCircle2 size={11} />
              {t("skills.page.evalLink")}
            </Link>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="flex gap-1">
            <Button variant="outline" size="sm" asChild>
              <Link
                to={`${ROUTES.PLAYGROUND}?skill=${encodeURIComponent(skill.id)}`}
              >
                <PlayCircle size={11} />
                {t("skills.page.tryInPlayground")}
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} disabled={busy}>
              <Pencil size={11} />
              {t("skills.page.editButton")}
            </Button>
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              aria-label={t("skills.page.deleteButton")}
              title={t("skills.page.deleteButton")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-danger-50 hover:text-danger-600 disabled:opacity-50"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-neutral-100 px-5 py-3">
          <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-neutral-800">
            <History size={12} />
            {t("skills.page.versionHistory")}
          </div>
          {versions === undefined ? (
            <p className="text-[11.5px] text-neutral-500">
              {t("skills.page.versionsLoading")}
            </p>
          ) : versions.length === 0 ? (
            <p className="text-[11.5px] text-neutral-500">
              {t("skills.page.versionsEmpty")}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {[...versions]
                .sort((a, b) => b.version - a.version)
                .map((v) => {
                  const isCurrent = v.version === skill.current_version;
                  return (
                    <li
                      key={v.version}
                      className="rounded border border-neutral-200 bg-white p-2.5"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="rounded bg-ocean-50 px-1.5 py-[1px] font-mono text-[10.5px] text-ocean-800">
                            v{v.version}
                          </span>
                          {isCurrent && (
                            <span className="rounded bg-success-50 px-1.5 py-[1px] font-mono text-[10.5px] text-success-700">
                              {t("skills.page.activeTag")}
                            </span>
                          )}
                          <span className="text-[10.5px] text-neutral-500">
                            {new Date(v.created_at).toLocaleString()}
                          </span>
                        </div>
                        {!isCurrent && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPromote(v.version)}
                            disabled={busy}
                          >
                            <RotateCcw size={11} />
                            {t("skills.page.promoteButton")}
                          </Button>
                        )}
                      </div>
                      {v.notes && (
                        <p className="mt-1 text-[11.5px] text-neutral-600">
                          {v.notes}
                        </p>
                      )}
                      <pre className="mono mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-neutral-50 p-2 text-[10.5px] leading-relaxed text-neutral-800">
                        {v.system_prompt}
                      </pre>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Empty state
// ============================================================================

function EmptyState({
  onCreate,
  onTemplate,
}: {
  onCreate(): void;
  onTemplate(payload: CreateSkillPayload): void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 p-6">
      <div className="flex items-center gap-2 text-ocean-900">
        <Layers size={16} />
        <span className="text-[14px] font-medium">
          {t("skills.page.emptyTitle")}
        </span>
      </div>
      <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-neutral-700">
        {t("skills.page.emptyBody")}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <TemplatePicker
          onPick={(payload) => onTemplate(payload)}
          variant="prominent"
        />
        <Button variant="outline" size="sm" onClick={onCreate}>
          <Plus size={12} />
          {t("skills.page.emptyCreate")}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Template picker — dropdown listing the 5 templates
// ============================================================================

function TemplatePicker({
  onPick,
  variant = "default",
}: {
  onPick(payload: CreateSkillPayload): void;
  variant?: "default" | "prominent";
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!wrapRef.current || !target) return;
      if (!wrapRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <Button
        variant={variant === "prominent" ? "default" : "outline"}
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Sparkles size={12} />
        {t("skills.page.fromTemplate")}
        <ChevronDown size={11} />
      </Button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 z-30 mt-1 w-[360px] overflow-hidden rounded-lg border border-neutral-200 bg-white py-1 shadow-lg"
        >
          {SKILL_TEMPLATES.map((tpl) => (
            <li key={tpl.id}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onPick(templateToPayload(tpl));
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left transition-colors hover:bg-ocean-50"
              >
                <div className="text-[12.5px] font-medium text-neutral-900">
                  {tpl.name}
                </div>
                <div className="mt-0.5 line-clamp-2 text-[11px] text-neutral-600">
                  {tpl.description}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Create dialog (used for empty/blank + template-prefilled flows)
// ============================================================================

interface CreateFormState {
  name: string;
  slug: string;
  description: string;
  system_prompt: string;
  model: string;
  temperature: string;
  notes: string;
}

function payloadToCreateForm(p: CreateSkillPayload | null): CreateFormState {
  return {
    name: p?.name ?? "",
    slug: p?.slug ?? "",
    description: p?.description ?? "",
    system_prompt: p?.system_prompt ?? "",
    model: p?.model ?? "",
    temperature:
      p?.parameters &&
      typeof (p.parameters as Record<string, unknown>).temperature === "number"
        ? String((p.parameters as Record<string, number>).temperature)
        : "0.4",
    notes: p?.notes ?? "",
  };
}

function CreateSkillDialog({
  open,
  onClose,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  initial: CreateSkillPayload | null;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<CreateFormState>(
    payloadToCreateForm(initial)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setState(payloadToCreateForm(initial));
      setError(null);
    }
  }, [open, initial]);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const temperature = state.temperature.trim()
        ? Number.parseFloat(state.temperature)
        : null;
      await SkillsApi.create({
        name: state.name,
        slug: state.slug.trim() || undefined,
        description: state.description.trim() || null,
        system_prompt: state.system_prompt,
        model: state.model.trim() || null,
        parameters:
          temperature !== null && !Number.isNaN(temperature)
            ? { temperature }
            : null,
        notes: state.notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [state, onSaved]);

  return (
    <Dialog open={open} onClose={onClose} size="max-w-2xl">
      <DialogHeader>
        <DialogTitle>
          {initial ? t("skills.page.fromTemplate") : t("skills.page.newButton")}
        </DialogTitle>
        <DialogDescription>
          {initial
            ? t("skills.form.templateDescription")
            : t("skills.form.blankDescription")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <Field label={t("skills.form.name")}>
          <input
            type="text"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            maxLength={120}
            className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          />
        </Field>
        <Field
          label={t("skills.form.slug")}
          hint={t("skills.form.slugHint")}
        >
          <input
            type="text"
            value={state.slug}
            onChange={(e) =>
              setState((s) => ({ ...s, slug: e.target.value.toLowerCase() }))
            }
            placeholder={t("skills.form.slugPlaceholder")}
            className="mono block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] outline-none focus:border-ocean-400"
          />
        </Field>
        <Field label={t("skills.form.description")}>
          <input
            type="text"
            value={state.description}
            onChange={(e) =>
              setState((s) => ({ ...s, description: e.target.value }))
            }
            maxLength={500}
            className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          />
        </Field>
        <Field
          label={t("skills.form.systemPrompt")}
          hint={
            initial
              ? t("skills.form.systemPromptTemplateHint")
              : undefined
          }
        >
          <textarea
            value={state.system_prompt}
            onChange={(e) =>
              setState((s) => ({ ...s, system_prompt: e.target.value }))
            }
            rows={initial ? 16 : 6}
            className="mono block w-full resize-y rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] leading-relaxed outline-none focus:border-ocean-400"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("skills.form.model")}>
            <ModelSelect
              value={state.model}
              onChange={(next) => setState((s) => ({ ...s, model: next }))}
            />
          </Field>
          <Field label={t("skills.form.temperature")}>
            <input
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={state.temperature}
              onChange={(e) =>
                setState((s) => ({ ...s, temperature: e.target.value }))
              }
              className="mono block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] outline-none focus:border-ocean-400"
            />
          </Field>
        </div>
        <Field label={t("skills.form.notes")}>
          <input
            type="text"
            value={state.notes}
            onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
            maxLength={500}
            className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          />
        </Field>
        {error && (
          <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-[11.5px] text-danger-700">
            {error}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("skills.form.cancel")}
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={submit}
          disabled={busy || !state.name.trim() || !state.system_prompt.trim()}
        >
          {busy ? t("skills.form.saving") : t("skills.form.saveNew")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ============================================================================
// Edit dialog (metadata + new immutable version)
// ============================================================================

function EditSkillDialog({
  open,
  skill,
  onClose,
  onChanged,
}: {
  open: boolean;
  skill: Skill;
  onClose(): void;
  onChanged(): void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description ?? "");
  const [systemPrompt, setSystemPrompt] = useState(
    skill.current_version_snapshot?.system_prompt ?? ""
  );
  const [model, setModel] = useState(
    skill.current_version_snapshot?.model ?? ""
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(skill.name);
      setDescription(skill.description ?? "");
      setSystemPrompt(skill.current_version_snapshot?.system_prompt ?? "");
      setModel(skill.current_version_snapshot?.model ?? "");
      setNotes("");
      setError(null);
    }
  }, [open, skill]);

  const saveMetadata = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await SkillsApi.update(skill.id, {
        name,
        description: description.trim() || null,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [skill.id, name, description, onChanged]);

  const appendVersion = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: AppendVersionPayload = {
        system_prompt: systemPrompt,
        model: model.trim() || null,
        notes: notes.trim() || null,
      };
      await SkillsApi.appendVersion(skill.id, payload);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [skill.id, systemPrompt, model, notes, onChanged]);

  return (
    <Dialog open={open} onClose={onClose} size="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t("skills.editDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("skills.editDialog.description")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="rounded border border-neutral-200 bg-neutral-50 p-3">
          <p className="mb-2 text-[12px] font-medium text-neutral-800">
            {t("skills.editDialog.metadataHeading")}
          </p>
          <Field label={t("skills.form.name")}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            />
          </Field>
          <Field label={t("skills.form.description")}>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            />
          </Field>
          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={saveMetadata}
              disabled={busy}
            >
              {busy
                ? t("skills.editDialog.savingMetadata")
                : t("skills.editDialog.saveMetadata")}
            </Button>
          </div>
        </div>

        <div className="mt-3 rounded border border-ocean-100 bg-ocean-50/30 p-3">
          <p className="mb-2 text-[12px] font-medium text-ocean-900">
            {t("skills.editDialog.newVersionHeading", {
              v: (skill.current_version ?? 0) + 1,
            })}
          </p>
          <Field label={t("skills.form.systemPrompt")}>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={10}
              className="mono block w-full resize-y rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] leading-relaxed outline-none focus:border-ocean-400"
            />
          </Field>
          <Field label={t("skills.form.model")}>
            <ModelSelect value={model} onChange={setModel} />
          </Field>
          <Field label={t("skills.form.notes")}>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              placeholder={t("skills.editDialog.notesPlaceholder")}
              className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            />
          </Field>
          <div className="mt-2 flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={appendVersion}
              disabled={busy || !systemPrompt.trim()}
            >
              {busy
                ? t("skills.editDialog.creatingVersion")
                : t("skills.editDialog.createVersion")}
            </Button>
          </div>
          <p className="mt-2 text-[10.5px] text-neutral-500">
            {t("skills.editDialog.versionsNote")}
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-[11.5px] text-danger-700">
            {error}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("skills.form.close")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ============================================================================
// Tiny helpers
// ============================================================================

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mt-2 block">
      <span className="mb-1 block text-[12px] font-medium text-neutral-800">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[10.5px] text-neutral-500">{hint}</span>
      )}
    </label>
  );
}

/**
 * Constrained-choice "Preferred model" dropdown for the skill form.
 *
 * Sourced from ``GET /v1/playground/available-models`` (the same catalog
 * the playground ModelChip uses). Empty string means "no preference"
 * (sent to the API as ``null``); the save flow keeps the ``""`` → null
 * mapping the free-text input already did.
 *
 * Edit-time edge case: if the skill's stored ``value`` is a model that
 * no longer appears in the active catalog (e.g. its provider was
 * revoked), we prepend a "(inactive)" entry preserving the stored
 * string so the operator can see what was saved + reselect to a valid
 * model. Empty catalog (no active credential) reduces the dropdown to
 * just "No preference" plus a hint linking to /providers.
 */
function ModelSelect({
  value,
  onChange,
}: {
  value: string;
  onChange(next: string): void;
}) {
  const { t } = useTranslation();
  const { catalog, loading, activeModels } = usePlaygroundCatalog();

  const trimmed = value.trim();
  const inCatalog = activeModels.includes(trimmed);
  const showInactive = trimmed !== "" && !inCatalog && !loading;
  const noActive = catalog?.has_active === false;

  return (
    <>
      <select
        value={trimmed}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="mono block w-full rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-[12px] outline-none focus:border-ocean-400 disabled:bg-neutral-50 disabled:text-neutral-400"
      >
        <option value="">{t("skills.form.modelNoPreference")}</option>
        {showInactive && (
          <option value={trimmed}>
            {t("skills.form.modelInactive", { model: trimmed })}
          </option>
        )}
        {activeModels.map((model) => (
          <option key={model} value={model}>
            {model}
          </option>
        ))}
      </select>
      {showInactive && (
        <span className="mt-1 block text-[10.5px] text-warning-700">
          {t("skills.form.modelInactiveHint")}
        </span>
      )}
      {noActive && !showInactive && (
        <span className="mt-1 block text-[10.5px] text-neutral-500">
          {t("skills.form.modelNoProviders")}{" "}
          <Link
            to={ROUTES.PROVIDERS}
            className="underline transition-colors hover:text-ocean-700"
          >
            {t("skills.form.modelNoProvidersCta")}
          </Link>
        </span>
      )}
    </>
  );
}
